import { useEffect, useRef, useState } from 'react';
import type { StreamBounds } from '../../../features/quickstart/types/theme';
import { Z_ANIMATIONS } from '../../../features/quickstart/config/zIndexLayers';

interface MobileFullscreenAnimationOverlayProps {
  animationUrl: string;
  backgroundImageUrl?: string;
  streamBounds: StreamBounds;
  isForceRotated?: boolean;
  visible: boolean;
  onDismiss?: () => void;
}

const hasNonZeroBounds = (bounds: StreamBounds): boolean =>
  bounds.width > 0 && bounds.height > 0;

const isVideoUrl = (url: string): boolean =>
  /\.(mp4|webm)(\?|$)/i.test(url);

/**
 * Mobile fullscreen animation overlay — plays an animated webp/avif/mp4 over the stream
 * until the user's first touch dismisses it. Blocks all input to the stream
 * beneath (consistent with other entry-gate overlays).
 *
 * Architecture matches SeamlessBackground: the component is ALWAYS in the DOM
 * and visibility is controlled via the `visible` prop. The video is never
 * unmounted, eliminating the iOS race where a freshly-mounted video flashes
 * native controls before JS can call .play().
 *
 * An optional backgroundImageUrl renders a static image behind the animation,
 * useful as an iOS fallback when video autoplay is blocked.
 */
export const MobileFullscreenAnimationOverlay = ({
  animationUrl,
  backgroundImageUrl,
  streamBounds,
  isForceRotated = false,
  visible,
  onDismiss,
}: MobileFullscreenAnimationOverlayProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const previousUrl = useRef(animationUrl);
  const useBounds = hasNonZeroBounds(streamBounds);
  const isVideo = isVideoUrl(animationUrl);

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    const video = videoRef.current;
    if (video && visible) {
      video.play().catch(() => { /* autoplay blocked */ });
    }
  };

  // Reset state when animationUrl changes
  useEffect(() => {
    if (animationUrl !== previousUrl.current) {
      setVideoLoaded(false);
      previousUrl.current = animationUrl;
    }
  }, [animationUrl]);

  // Play/pause video based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (visible) {
      video.currentTime = 0;
      video.muted = true;
      video.play().catch(() => { /* autoplay blocked until user gesture */ });
    } else {
      video.pause();
    }
  }, [visible, animationUrl]);


  const rotatedStyle: React.CSSProperties = isForceRotated
    ? {
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: '100vh',
        height: '100vw',
        maxWidth: '100dvh',
        transform: 'translate(-50%, -50%) rotate(var(--force-rotate-angle, 90deg))',
        transformOrigin: 'center center',
        zIndex: Z_ANIMATIONS,
      }
    : {
        position: useBounds ? 'absolute' : 'fixed',
        zIndex: Z_ANIMATIONS,
        ...(useBounds
          ? {
              top: streamBounds.top,
              left: streamBounds.left,
              width: streamBounds.width,
              height: streamBounds.height,
            }
          : { inset: 0 }),
      };

  return (
    <div
      style={{
        ...rotatedStyle,
        opacity: visible ? 1 : 0,
        transition: 'opacity 300ms ease-in-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
      data-gamepad-input
      onTouchEnd={(e) => {
        if (!visible) return;
        e.preventDefault();
        onDismiss?.();
      }}
      onClick={() => {
        if (!visible) return;
        onDismiss?.();
      }}
    >
      {/* Background image layer (behind animation) — height-locked, centered.
         When rotated, CSS axes swap: container width = visual height, so use w-full h-auto. */}
      {backgroundImageUrl && (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
          <img
            src={backgroundImageUrl}
            alt=""
            className={isForceRotated ? 'w-full h-auto max-h-none' : 'h-full w-auto max-w-none'}
            draggable={false}
          />
        </div>
      )}

      {/* Animation layer (on top) — height-locked, centered */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
        {isVideo ? (
          <video
            ref={videoRef}
            src={animationUrl}
            autoPlay
            muted
            loop
            playsInline
            preload='auto'
            data-overlay-video
            onLoadedData={handleVideoLoaded}
            className={isForceRotated ? 'w-full h-auto max-h-none' : 'h-full w-auto max-w-none'}
            style={{
              opacity: videoLoaded ? 1 : 0,
              transition: 'opacity 200ms ease-in',
            }}
          />
        ) : (
          <img
            src={animationUrl}
            alt=""
            className={isForceRotated ? 'w-full h-auto max-h-none' : 'h-full w-auto max-w-none'}
            draggable={false}
          />
        )}
      </div>
    </div>
  );
};
